/**
 * Ein Datum direkt in der Zeile ändern: Ein Klick auf die Anzeige öffnet den Kalender darunter.
 * Gespeichert wird mit dem Tag – Jahr und Monat wählen nur vor. Wie das Datum aussieht, gibt
 * der Aufrufer als `children` vor.
 */
import React, { useState } from "react";
import { Popover } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { format, isValid, parseISO } from "date-fns";
import InlineEditable, {
  InlineEditableHeading,
  InlineEditableProps,
} from "./InlineEditable";

interface InlineDatePickerProps extends InlineEditableProps {
  /** ISO-Datum; leer, wenn noch keins gesetzt ist */
  value?: string | null;
  /** Nur bei einem anderen Tag als bisher */
  onChange: (iso: string) => void;
}

const InlineDatePicker: React.FC<InlineDatePickerProps> = ({
  value,
  onChange,
  label,
  ...editable
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <InlineEditable
        {...editable}
        kind="date"
        open={!!anchor}
        onOpen={setAnchor}
      />
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <InlineEditableHeading label={label} />
        {anchor && (
          <DateCalendar
            value={value ? parseISO(value) : null}
            onChange={(next: Date | null, selectionState) => {
              if (selectionState && selectionState !== "finish") return;
              setAnchor(null);
              if (!next || !isValid(next)) return;
              const iso = format(next, "yyyy-MM-dd");
              if (iso !== value) onChange(iso);
            }}
          />
        )}
      </Popover>
    </>
  );
};

export default InlineDatePicker;
